/**
 * Audio engine: mic capture + Opus encode/decode via Web Codecs + per-sender
 * 3D rendering through Web Audio PannerNode HRTF.
 *
 * Per-sender graph (lazy-built the first time we see an AGID):
 *
 *     opus -> AudioDecoder -> mono Float32
 *          -> postMessage to that sender's `mono-source` AudioWorkletNode
 *          -> GainNode (user * master * !mute * distance)
 *          -> PannerNode (HRTF, positioned at listener-local localX/Y/Z)
 *          -> AudioContext.destination
 *
 * The PannerNode does the actual binaural rendering using the browser's
 * built-in HRIR convolution; output is true 3D headphone audio (ITD + ILD +
 * spectral cues + front/back disambiguation). When the bridge omits the
 * `localX/Y/Z` fields (old bridge, talking to us during a rollout) we fall
 * back to the legacy gainL/gainR multipliers - same behaviour as 0.0
 * position with constant-power amplitude pan.
 *
 * Per-sender state lives in `senders` map keyed by AGID. `setSenderSpatial`,
 * `setSenderUserGain`, `setSenderMuted`, `setMasterGain` mutate it; the
 * decode path reads it on every frame.
 *
 * Browser support: requires Web Codecs AudioEncoder/AudioDecoder. Available
 * on Safari 16.4+, Chrome 94+, Firefox 130+. Older browsers fail gracefully
 * with an early throw before mic permission is requested. PannerNode HRTF is
 * universally supported in any browser that ships Web Audio.
 */

import { KbpsTracker, lineariseRing, writeRing, type RingState } from './audioHelpers.ts'
import micCaptureUrl from './worklets/mic-capture.ts?worker&url'
import monoSourceUrl from './worklets/mono-source.ts?worker&url'

const SAMPLE_RATE = 48_000
const FRAME_SAMPLES = 960 // 20 ms
const OPUS_BITRATE = 32_000

/**
 * Constant-power center pan: when no spatial gain is known for a sender
 * (first packet before snapshot lands, etc.) we play them at 1/sqrt(2)
 * in each ear so the combined energy equals 1.0.
 */
const CENTER_GAIN = 0.70710678

/**
 * Mark a sender as "talking" if they sent a frame in the last N ms.
 * Cleared on the next polled tick so the UI dot stops glowing when a
 * speaker pauses.
 */
const TALKING_WINDOW_MS = 250

/**
 * Sync fast-path check: the Web Codecs AudioEncoder/AudioDecoder constructors
 * exist. Returns the list of missing constructor names so the caller can
 * surface a precise error to the user (e.g. "AudioEncoder missing => need
 * iOS 18.4 or later, you're probably on iOS 17").
 *
 *   AudioDecoder: Safari 16.4+, Chrome 94+, Firefox 130+
 *   AudioEncoder: Safari 18.4+, Chrome 94+, Firefox 130+ (Safari shipped
 *                 decoder first, encoder ~2 years later)
 *
 * A return of [] means both constructors exist; the Opus config still has
 * to be negotiable via {@link negotiateOpusConfig}.
 */
export function missingWebCodecsApis(): ReadonlyArray<'AudioEncoder' | 'AudioDecoder'> {
  const missing: Array<'AudioEncoder' | 'AudioDecoder'> = []
  if (typeof (globalThis as { AudioEncoder?: unknown }).AudioEncoder !== 'function') {
    missing.push('AudioEncoder')
  }
  if (typeof (globalThis as { AudioDecoder?: unknown }).AudioDecoder !== 'function') {
    missing.push('AudioDecoder')
  }
  return missing
}

/**
 * Back-compat boolean wrapper around {@link missingWebCodecsApis}. Prefer
 * the array form when you want to render a precise error message.
 */
export function webCodecsSupported(): boolean {
  return missingWebCodecsApis().length === 0
}

/**
 * Per-config probe result, kept verbose so error UIs can render exactly
 * what was tried and what the browser said.
 */
export interface ProbeResult {
  /** Human label for the config shape we tried. */
  readonly label: string
  /** Verbatim config we passed to isConfigSupported. */
  readonly config: Record<string, unknown>
  /** Browser response: supported / unsupported, or 'threw' with the throw. */
  readonly status: 'supported' | 'unsupported' | 'threw' | 'skipped'
  /** Set only when status === 'threw'. */
  readonly error?: string
}

/**
 * Full audio-stack feature report. Every probe runs unconditionally so
 * the user gets a complete picture (e.g. "AudioDecoder is present but
 * AudioEncoder is missing") instead of guesswork.
 */
export interface AudioDiagnostics {
  /** typeof AudioEncoder === 'function' */
  readonly audioEncoder: 'present' | 'missing'
  /** typeof AudioDecoder === 'function' */
  readonly audioDecoder: 'present' | 'missing'
  /** typeof AudioContext === 'function' (also covers webkitAudioContext) */
  readonly audioContext: 'present' | 'missing'
  /**
   * typeof WebAssembly === 'undefined' is a high-confidence Lockdown Mode
   * indicator on iOS Safari (Lockdown disables WASM too). 'missing' on
   * iOS Safari ~= Lockdown Mode is on.
   */
  readonly webAssembly: 'present' | 'missing'
  /** Per-encoder-config probe results, in the order tried. */
  readonly encoderProbes: ReadonlyArray<ProbeResult>
  /** Per-decoder-config probe results, in the order tried. */
  readonly decoderProbes: ReadonlyArray<ProbeResult>
  /**
   * Final outcome:
   *   'ok'                  : both encoder and decoder negotiated.
   *   'no-encoder-api'      : AudioEncoder constructor missing.
   *   'no-decoder-api'      : AudioDecoder constructor missing.
   *   'no-encoder-config'   : encoder present but no Opus config accepted.
   *   'no-decoder-config'   : decoder present but no Opus config accepted.
   */
  readonly outcome:
    | 'ok'
    | 'no-encoder-api'
    | 'no-decoder-api'
    | 'no-encoder-config'
    | 'no-decoder-config'
}

/**
 * Single entry point used by the voice page to decide whether the browser
 * can run the client and, when it can't, to render an exact failure
 * report. Always returns diagnostics (even on success) so the result is
 * loggable for telemetry.
 *
 * Replaces the two-step (webCodecsSupported + negotiateOpusConfig) probe
 * with a single all-in-one call. The legacy functions stay for unit-test
 * back-compat.
 */
export async function probeAudioSupport(): Promise<{
  config: NegotiatedOpusConfig | null
  diagnostics: AudioDiagnostics
}> {
  const g = globalThis as {
    AudioEncoder?: unknown
    AudioDecoder?: unknown
    AudioContext?: unknown
    webkitAudioContext?: unknown
    WebAssembly?: unknown
  }
  const hasEncoder = typeof g.AudioEncoder === 'function'
  const hasDecoder = typeof g.AudioDecoder === 'function'
  const hasCtx = typeof g.AudioContext === 'function'
    || typeof g.webkitAudioContext === 'function'
  const hasWasm = typeof g.WebAssembly !== 'undefined'

  const tunedEncoder: AudioEncoderConfig = {
    codec: 'opus',
    sampleRate: SAMPLE_RATE,
    numberOfChannels: 1,
    bitrate: OPUS_BITRATE,
    opus: {
      application: 'voip',
      frameDuration: 20_000,
      signal: 'voice',
      complexity: 5,
    } as unknown as Record<string, unknown>,
  } as AudioEncoderConfig
  const minimalEncoder: AudioEncoderConfig = {
    codec: 'opus',
    sampleRate: SAMPLE_RATE,
    numberOfChannels: 1,
    bitrate: OPUS_BITRATE,
  }
  const decoderConfig: AudioDecoderConfig = {
    codec: 'opus',
    sampleRate: SAMPLE_RATE,
    numberOfChannels: 1,
  }

  const encoderProbes: ProbeResult[] = []
  let chosenEncoder: AudioEncoderConfig | null = null
  if (hasEncoder) {
    for (const [label, cfg] of [
      ['tuned (opus.signal=voice, application=voip)', tunedEncoder],
      ['minimal (opus only)', minimalEncoder],
    ] as const) {
      try {
        const res = await AudioEncoder.isConfigSupported(cfg)
        const supported = res.supported === true
        encoderProbes.push({
          label,
          config: cfg as unknown as Record<string, unknown>,
          status: supported ? 'supported' : 'unsupported',
        })
        if (supported && chosenEncoder === null) {
          chosenEncoder = (res.config ?? cfg) as AudioEncoderConfig
        }
      } catch (err) {
        encoderProbes.push({
          label,
          config: cfg as unknown as Record<string, unknown>,
          status: 'threw',
          error: err instanceof Error ? err.message : String(err),
        })
      }
    }
  } else {
    encoderProbes.push({
      label: 'tuned (opus.signal=voice, application=voip)',
      config: tunedEncoder as unknown as Record<string, unknown>,
      status: 'skipped',
    })
    encoderProbes.push({
      label: 'minimal (opus only)',
      config: minimalEncoder as unknown as Record<string, unknown>,
      status: 'skipped',
    })
  }

  const decoderProbes: ProbeResult[] = []
  let chosenDecoder: AudioDecoderConfig | null = null
  if (hasDecoder) {
    try {
      const res = await AudioDecoder.isConfigSupported(decoderConfig)
      const supported = res.supported === true
      decoderProbes.push({
        label: 'opus 48kHz mono',
        config: decoderConfig as unknown as Record<string, unknown>,
        status: supported ? 'supported' : 'unsupported',
      })
      if (supported) chosenDecoder = (res.config ?? decoderConfig) as AudioDecoderConfig
    } catch (err) {
      decoderProbes.push({
        label: 'opus 48kHz mono',
        config: decoderConfig as unknown as Record<string, unknown>,
        status: 'threw',
        error: err instanceof Error ? err.message : String(err),
      })
    }
  } else {
    decoderProbes.push({
      label: 'opus 48kHz mono',
      config: decoderConfig as unknown as Record<string, unknown>,
      status: 'skipped',
    })
  }

  let outcome: AudioDiagnostics['outcome']
  if (!hasEncoder) outcome = 'no-encoder-api'
  else if (!hasDecoder) outcome = 'no-decoder-api'
  else if (chosenEncoder === null) outcome = 'no-encoder-config'
  else if (chosenDecoder === null) outcome = 'no-decoder-config'
  else outcome = 'ok'

  const diagnostics: AudioDiagnostics = {
    audioEncoder: hasEncoder ? 'present' : 'missing',
    audioDecoder: hasDecoder ? 'present' : 'missing',
    audioContext: hasCtx ? 'present' : 'missing',
    webAssembly: hasWasm ? 'present' : 'missing',
    encoderProbes,
    decoderProbes,
    outcome,
  }

  const config = chosenEncoder !== null && chosenDecoder !== null
    ? { encoder: chosenEncoder, decoder: chosenDecoder }
    : null
  return { config, diagnostics }
}

export interface NegotiatedOpusConfig {
  readonly encoder: AudioEncoderConfig
  readonly decoder: AudioDecoderConfig
}

/**
 * Resolve the strictest Opus encoder + decoder configs the current browser
 * can actually accept, using AudioEncoder.isConfigSupported probes.
 *
 * Chrome / Edge / Chromium accept the full config with `opus.signal` and
 * `opus.complexity` extensions, which tune the encoder for voice and pick a
 * lower-CPU complexity tier. Safari 18.4+ added AudioEncoder for Opus but
 * rejects those Chromium-specific extensions outright; the encoder errors
 * silently and the user hears nothing. We try the tuned config first and
 * fall back to a minimal spec-compliant config so Safari users still get
 * audio (just without the Chrome-only optimisations).
 *
 * Returns null when no Opus config is supported - typically Safari < 18.4,
 * the iOS Safari long tail (iOS < 18.4), or browsers without WebCodecs at
 * all. Callers should treat null as "show the Browser-not-supported page."
 */
export async function negotiateOpusConfig(): Promise<NegotiatedOpusConfig | null> {
  if (!webCodecsSupported()) return null

  const tunedEncoder: AudioEncoderConfig = {
    codec: 'opus',
    sampleRate: SAMPLE_RATE,
    numberOfChannels: 1,
    bitrate: OPUS_BITRATE,
    opus: {
      application: 'voip',
      frameDuration: 20_000,
      signal: 'voice',
      complexity: 5,
    } as unknown as Record<string, unknown>,
  } as AudioEncoderConfig
  const minimalEncoder: AudioEncoderConfig = {
    codec: 'opus',
    sampleRate: SAMPLE_RATE,
    numberOfChannels: 1,
    bitrate: OPUS_BITRATE,
  }
  const decoder: AudioDecoderConfig = {
    codec: 'opus',
    sampleRate: SAMPLE_RATE,
    numberOfChannels: 1,
  }

  const encoder = await pickSupported<AudioEncoderConfig>(
    [tunedEncoder, minimalEncoder],
    async (c) => {
      const r = await AudioEncoder.isConfigSupported(c)
      return r.supported === true ? (r.config ?? c) : null
    },
  )
  if (encoder === null) return null

  const dec = await pickSupported<AudioDecoderConfig>(
    [decoder],
    async (c) => {
      const r = await AudioDecoder.isConfigSupported(c)
      return r.supported === true ? (r.config ?? c) : null
    },
  )
  if (dec === null) return null

  return { encoder, decoder: dec }
}

async function pickSupported<T>(
  candidates: ReadonlyArray<T>,
  probe: (c: T) => Promise<T | null>,
): Promise<T | null> {
  for (const c of candidates) {
    try {
      const result = await probe(c)
      if (result !== null) return result
    } catch {
      // isConfigSupported can throw on entirely unrecognized shapes (older
      // implementations). Treat that as "not supported" and move on.
    }
  }
  return null
}

interface SenderState {
  agid: string
  decoder: AudioDecoder
  /**
   * One mono streaming source worklet, one GainNode for amplitude (distance
   * * user * master * !mute), one PannerNode for HRTF placement. Wired as
   *
   *   source -> gain -> panner -> ctx.destination
   *
   * The chain is built lazily the first time we hear from this AGID and torn
   * down on `forgetSender`. PannerNode position is updated in setSpatialGains
   * from the bridge's listener-local coords; GainNode value is updated from
   * the bridge's distance * user prefs each snapshot tick.
   */
  source: AudioWorkletNode
  gainNode: GainNode
  panner: PannerNode
  /**
   * Most recently applied spatial values. Kept on the JS side so that user-
   * gain or mute toggles can recompute the GainNode value without needing
   * the snapshot to fire again.
   */
  distanceGain: number
  userGain: number
  muted: boolean
  /**
   * Legacy stereo gains from the bridge. Retained so old bridges (no
   * localX/Y/Z) can still drive a flat amplitude pan. New bridges supply
   * positions; we ignore these in that path.
   */
  gainL: number
  gainR: number
  lastPacketMs: number
  /**
   * Wall-clock time of the most recent snapshot frame that supplied a
   * non-null spatial gain for this sender. Used by {@link
   * AudioEngine.setSpatialGains} as a grace-period anchor: if the new
   * snapshot frame omits this sender, we keep their previous gain for
   * SPATIAL_GAIN_GRACE_MS before zeroing them out. Without this, a
   * single late snapshot frame (network jitter on the snapshot WS, brief
   * RCON gap) silenced everyone for one tick.
   */
  lastGainUpdateMs: number
}

/**
 * How long a sender's most recent non-zero spatial gain is honoured after
 * the next snapshot frame omits them. Sized to cover one missed snapshot
 * tick at the default 1 s poll plus generous jitter; large enough to mask
 * routine WS lag, small enough that a sender who genuinely moves out of
 * range falls silent quickly.
 */
const SPATIAL_GAIN_GRACE_MS = 2000

/**
 * Self-diagnostics surfaced to the UI. Mirrors the desktop client's
 * LiveVoiceStats. Encoder bitrate is averaged over the most recent
 * 1-second window so a brief PTT burst isn't lost in a long average.
 */
export interface AudioStats {
  readonly packetsSent: number
  readonly bytesSent: number
  readonly kbpsRecent: number
  readonly micPeak: number
}

/** Linear amplitude -> dBFS, clamped at -120 dB so log(0) is finite. */
export function linearToDbfs(linear: number): number {
  if (linear <= 0) return -120
  return 20 * Math.log10(linear)
}

/** dBFS -> linear amplitude. Inverse of linearToDbfs. */
export function dbfsToLinear(db: number): number {
  return 10 ** (db / 20)
}

/**
 * 30-second per-speaker ring buffer of decoded mono Float32 frames. Used
 * by the moderation clip capture feature: a "save last 30s" button pulls
 * a snapshot of every speaker we heard in that window, zips them as WAVs,
 * and triggers a download. Mirrors the per-speaker leg of the desktop
 * client's VoiceClipBuffer.
 *
 * Storage is fixed-size: at 48 kHz mono * 30 s * 4 bytes/sample =
 * 5.76 MB per speaker. We cap at CLIP_MAX_SPEAKERS to keep the
 * worst-case footprint bounded; older speakers get evicted FIFO when
 * the cap is hit.
 *
 * We deliberately omit a combined "mix" track. In the desktop client
 * the spatial mixer produces a single time-aligned stereo stream, but on
 * the web each sender's onDecoded callback fires independently and is
 * not slot-synchronised, so summing into a shared ring would either
 * accumulate stale samples or drop simultaneous talkers. Admins can play
 * the per-speaker WAVs side by side in any DAW.
 */
export interface ClipBufferSnapshot {
  readonly sampleRate: number
  readonly capturedAt: Date
  readonly durationSeconds: number
  readonly perSpeaker: ReadonlyMap<string, Float32Array>
}

export interface AudioEngineOptions {
  /**
   * Called with a fresh Opus-encoded packet ready for the bridge. The
   * engine doesn't know about the WS - the connection layer attaches.
   */
  readonly onOpusPacket: (opus: Uint8Array) => void
  /**
   * Pre-negotiated Opus configs from {@link negotiateOpusConfig}. Required so
   * Safari (which rejects the Chromium-only `opus.signal` / `opus.complexity`
   * extensions) gets a config it can actually accept. Without this the
   * encoder configures itself with the tuned shape and Safari silently
   * fails through the error callback.
   */
  readonly opusConfig: NegotiatedOpusConfig
  /**
   * Optional callback fired when the encoder/decoder errors out at runtime
   * (e.g. a config Safari probes as supported but rejects on actual use).
   * Surface this to the UI so the user knows why nothing transmits. The
   * page-level fatal banner is the right destination.
   */
  readonly onCodecError?: (where: 'encoder' | 'decoder', err: unknown) => void
}

export interface StartMicOptions {
  /** deviceId from enumerateDevices() or null for browser default. */
  readonly deviceId?: string | null
  /** Browser-native noise suppression, AEC, and AGC. Defaults to true. */
  readonly noiseSuppression?: boolean
}

const CLIP_WINDOW_SECONDS = 30
const CLIP_WINDOW_SAMPLES = SAMPLE_RATE * CLIP_WINDOW_SECONDS
const CLIP_MAX_SPEAKERS = 12

/**
 * Per-speaker ring of decoded mono samples. State (writeIndex, filled) lives
 * inline so the shared writeRing helper from audioHelpers.ts can mutate it.
 * `lastWriteMs` is the FIFO-eviction key when CLIP_MAX_SPEAKERS is hit.
 */
interface ClipRing extends RingState {
  readonly data: Float32Array
  lastWriteMs: number
}

export class AudioEngine {
  private readonly opts: AudioEngineOptions
  private ctx: AudioContext | null = null
  /**
   * Master limiter sitting between every sender's PannerNode and the
   * destination. Senders are boosted by masterGain (2.0) * userGain (up to
   * 4) and then summed; with one loud speaker, or several at once, the sum
   * exceeds Float32 full-scale and the audio hardware hard-clips, which the
   * listener hears as crackle / distortion. A DynamicsCompressorNode tuned
   * as a brickwall limiter rides the peaks down without per-sample clipping,
   * mirroring the desktop SpatialMixer's limiter stage (the per-sender
   * PannerNode path lost that stage when it replaced the old summed
   * playback-ring worklet).
   */
  private masterLimiter: DynamicsCompressorNode | null = null
  private encoder: AudioEncoder | null = null
  private micStream: MediaStream | null = null
  private micWorklet: AudioWorkletNode | null = null
  /** Keyed by AGID. Each entry owns one stateful Opus decoder. */
  private readonly senders = new Map<string, SenderState>()
  /** sessionId -> agid mapping, populated by voiceSession events. */
  private readonly sessionToAgid = new Map<number, string>()
  /** Maps the WS-supplied per-packet AGID for senders we haven't yet seen
   *  via voiceSession. Lazy registration mirrors the bridge's UDP path. */
  private masterGain = 2.0
  // Wall-clock microseconds used as a monotonic timestamp for AudioData.
  // Web Codecs requires unique increasing timestamps per encode call.
  private encodeTsUs = 0
  private muted = true

  /**
   * Transmission mode. 'ptt' (default) honours `setMuted` for every frame -
   * caller drives mute via the PTT button. 'openMic' ignores `setMuted` and
   * uses a peak-amplitude noise gate (`_vadThresholdPeak`) to decide which
   * frames to forward. Matches the desktop / mobile client semantics.
   */
  private mode: 'ptt' | 'openMic' = 'ptt'

  /** Peak-amplitude threshold (linear 0..1) used by the open-mic VAD. */
  private vadThresholdPeak = 0.012

  /**
   * How long after the last above-threshold frame to keep transmitting.
   * Stops a word from clipping when the speaker pauses briefly. Matches
   * the desktop client's `MicCapture.VadHangover`.
   */
  private static readonly VAD_HANGOVER_MS = 300
  private lastVoiceMs = 0
  /** Most recent frame's peak amplitude. Surfaced for the UI's mic meter. */
  private lastPeak = 0

  /** Self-diagnostics counters. Mirrored to UI by getStats(). */
  private readonly kbpsTracker = new KbpsTracker()

  /**
   * Per-AGID 30s clip ring. Lazy: only allocated when a sender actually
   * transmits a frame, evicted FIFO when CLIP_MAX_SPEAKERS is hit.
   */
  private readonly clipRings = new Map<string, ClipRing>()

  /** Display-name labels for each AGID, supplied by the snapshot stream
   *  via setSpeakerLabel(). Used by clip exporters to name WAVs by player. */
  private readonly speakerLabels = new Map<string, string>()

  constructor(opts: AudioEngineOptions) {
    this.opts = opts
  }

  /**
   * Initialise the audio context + worklets. Must run inside a user-gesture
   * handler on iOS Safari, otherwise AudioContext stays suspended and no
   * sound plays. The Voice page calls this from the Connect button's click
   * handler synchronously.
   */
  async init(): Promise<void> {
    if (this.ctx !== null) return
    const missing = missingWebCodecsApis()
    if (missing.length > 0) {
      // Tell the user exactly which constructor is missing so a Safari 17
      // tester ("I have a recent iPhone, why doesn't it work?") gets a
      // useful answer instead of a vague "needs 16.4+" (which is true for
      // AudioDecoder but not AudioEncoder).
      const needs = missing.includes('AudioEncoder')
        ? 'iOS Safari 18.4+ / macOS Safari 18.4+ / Chrome 94+ / Firefox 130+'
        : 'Safari 16.4+ / Chrome 94+ / Firefox 130+'
      throw new Error(
        `Browser missing Web Codecs ${missing.join(' + ')}. Update your browser. Required: ${needs}.`,
      )
    }
    const ctx = new AudioContext({ sampleRate: SAMPLE_RATE, latencyHint: 'interactive' })
    if (ctx.state === 'suspended') {
      await ctx.resume()
    }
    this.ctx = ctx
    // Master limiter on the output bus. Every sender's panner connects here
    // instead of straight to the destination so the summed, gain-boosted
    // signal can't hard-clip the output device. Threshold just under 0 dBFS,
    // high ratio + zero knee = brickwall limiting; fast attack catches
    // transients, moderate release avoids pumping on continuous speech.
    const limiter = ctx.createDynamicsCompressor()
    limiter.threshold.value = -1.0
    limiter.knee.value = 0
    limiter.ratio.value = 20
    limiter.attack.value = 0.003
    limiter.release.value = 0.25
    limiter.connect(ctx.destination)
    this.masterLimiter = limiter
    // Load the per-sender mono streaming source worklet. Each new sender
    // will instantiate one of these and pipe through GainNode -> PannerNode.
    await ctx.audioWorklet.addModule(monoSourceUrl)
    // Reorient the AudioListener so our convention (+X = right, +Y = forward,
    // +Z = up) lines up with the bridge's coords. Default Web Audio listener
    // faces -Z which would mean swapping axes per-frame.
    this.configureListener(ctx)
  }

  /**
   * Set the AudioListener forward = +Y / up = +Z so we can feed bridge-supplied
   * listener-local positions straight into PannerNode.position* AudioParams.
   *
   * Two API generations exist: the modern AudioParam form (Chrome 79+, Firefox
   * 70+, Safari 14.1+) and the legacy setOrientation() method (anything that
   * shipped Web Audio). We feature-detect because Safari 14.0 and a long tail
   * of mobile WebView builds only have the legacy form.
   */
  private configureListener(ctx: AudioContext): void {
    const listener = ctx.listener as AudioListener & {
      forwardX?: AudioParam
      forwardY?: AudioParam
      forwardZ?: AudioParam
      upX?: AudioParam
      upY?: AudioParam
      upZ?: AudioParam
      positionX?: AudioParam
      positionY?: AudioParam
      positionZ?: AudioParam
    }
    if (listener.forwardX !== undefined) {
      listener.positionX!.value = 0
      listener.positionY!.value = 0
      listener.positionZ!.value = 0
      listener.forwardX.value = 0
      listener.forwardY!.value = 1 // forward = +Y
      listener.forwardZ!.value = 0
      listener.upX!.value = 0
      listener.upY!.value = 0
      listener.upZ!.value = 1 // up = +Z
    } else if (typeof listener.setOrientation === 'function') {
      // Legacy API. Same orientation as above.
      listener.setOrientation(0, 1, 0, 0, 0, 1)
      if (typeof listener.setPosition === 'function') listener.setPosition(0, 0, 0)
    }
  }

  /**
   * Acquire the mic and wire up the encoder. Safe to call after init().
   * Calling more than once is a no-op (returns immediately).
   *
   * Options select which mic device + whether to apply browser-native
   * noise suppression / AEC / AGC. Persisted user preferences live in
   * auth.ts; the page passes them through here so the engine stays
   * storage-agnostic.
   */
  async startMic(opts: StartMicOptions = {}): Promise<void> {
    if (this.micStream !== null) return
    const ctx = this.requireCtx()

    const ns = opts.noiseSuppression !== false
    const audioConstraints: MediaTrackConstraints = {
      echoCancellation: ns,
      noiseSuppression: ns,
      autoGainControl: ns,
      sampleRate: SAMPLE_RATE,
      channelCount: 1,
    }
    if (opts.deviceId !== null && opts.deviceId !== undefined && opts.deviceId.length > 0) {
      audioConstraints.deviceId = { exact: opts.deviceId }
    }
    const stream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints })
    this.micStream = stream

    await ctx.audioWorklet.addModule(micCaptureUrl)
    const source = ctx.createMediaStreamSource(stream)
    const worklet = new AudioWorkletNode(ctx, 'mic-capture', {
      numberOfInputs: 1,
      numberOfOutputs: 0,
      channelCount: 1,
      channelCountMode: 'explicit',
    })
    source.connect(worklet)
    this.micWorklet = worklet

    const encoder = new AudioEncoder({
      output: (chunk) => {
        if (this.muted) return
        const opus = new Uint8Array(chunk.byteLength)
        chunk.copyTo(opus)
        this.recordSentBytes(opus.byteLength)
        this.opts.onOpusPacket(opus)
      },
      error: (e) => {
        // Promote from console-only to caller hook so the voice page can
        // surface a clear "your browser claimed to support this codec but
        // rejected it at runtime" banner. Most often hits Safari versions
        // that announce AudioEncoder support but quietly reject Opus.
        console.error('AudioEncoder error', e)
        this.opts.onCodecError?.('encoder', e)
      },
    })
    try {
      encoder.configure(this.opts.opusConfig.encoder)
    } catch (err) {
      // Synchronous configure() throws on a malformed config shape. Should
      // not happen for us since we negotiated this exact config via
      // isConfigSupported, but defensive: report and re-throw so startMic
      // rejects rather than continuing with a broken encoder.
      this.opts.onCodecError?.('encoder', err)
      throw err
    }
    this.encoder = encoder

    worklet.port.onmessage = (e: MessageEvent): void => {
      const buf = e.data as ArrayBuffer
      const samples = new Float32Array(buf)
      if (samples.length !== FRAME_SAMPLES) return

      // VU/peak meter feed - computed regardless of mode so the UI bar
      // moves even while the user is silently tuning the gate.
      let peak = 0
      for (let i = 0; i < samples.length; i += 1) {
        const a = samples[i]! < 0 ? -samples[i]! : samples[i]!
        if (a > peak) peak = a
      }
      this.lastPeak = peak

      // Transmission gate: PTT mode honours `muted` directly. Open mic mode
      // uses the VAD with hangover so brief pauses don't clip the tail of a
      // word. Mirrors the C# client's MicCapture.PassesVad logic.
      let pass = false
      if (this.mode === 'ptt') {
        pass = !this.muted
      } else {
        const now = Date.now()
        if (peak >= this.vadThresholdPeak) {
          this.lastVoiceMs = now
          pass = true
        } else {
          pass = now - this.lastVoiceMs <= AudioEngine.VAD_HANGOVER_MS
        }
      }
      if (!pass) return

      const audioData = new AudioData({
        format: 'f32-planar',
        sampleRate: SAMPLE_RATE,
        numberOfFrames: FRAME_SAMPLES,
        numberOfChannels: 1,
        timestamp: this.encodeTsUs,
        data: samples,
      })
      this.encodeTsUs += 20_000
      try { encoder.encode(audioData) } catch (err) { console.warn('encode failed', err) }
      audioData.close()
    }
  }

  setMuted(muted: boolean): void {
    this.muted = muted
  }

  /**
   * Switch between PTT and open-mic transmission modes.
   * - 'ptt': encode + send only while the user holds the PTT button.
   * - 'openMic': encode + send when peak amplitude crosses the VAD
   *   threshold (with hangover so brief pauses don't clip).
   */
  setMode(mode: 'ptt' | 'openMic'): void {
    this.mode = mode
    // Reset the hangover timer when switching INTO open mic so we don't
    // immediately transmit a buffered burst that pre-dates the user's
    // explicit consent to go live.
    if (mode === 'openMic') this.lastVoiceMs = 0
  }

  /** Linear peak threshold (0..1) used by open-mic VAD. */
  setVadThreshold(value: number): void {
    this.vadThresholdPeak = Math.max(0, Math.min(1, value))
  }

  /**
   * Latest captured peak amplitude (0..1). Polled by the UI to render a
   * live mic meter while the user is tuning the noise-gate slider. Always
   * updated regardless of mode so muted-but-live cases (PTT released)
   * still get a visual confirmation that the mic is feeding samples.
   */
  getPeak(): number {
    return this.lastPeak
  }

  /**
   * Re-prime the audio pipeline after the page was backgrounded. Mobile
   * browsers auto-suspend AudioContext on lock; without this call, even
   * a successful WS reconnect plays no sound. Idempotent and safe to
   * call when nothing is wrong.
   *
   * Returns the cached startMic options so the caller can decide whether
   * to restart the mic stream (some browsers end the track on lock and
   * we can't recover without going through getUserMedia again).
   */
  async resumeAfterWake(): Promise<{ micTrackEnded: boolean }> {
    const ctx = this.ctx
    if (ctx !== null && ctx.state === 'suspended') {
      try { await ctx.resume() } catch (err) {
        console.warn('audio: ctx.resume failed', err)
      }
    }
    const stream = this.micStream
    const track = stream?.getAudioTracks()[0]
    const ended = track?.readyState === 'ended'
    return { micTrackEnded: ended === true }
  }

  /**
   * Drop the existing mic stream + worklet + encoder and rebuild from
   * scratch. Used by the wake handler when the OS ended the mic track
   * during a long lock; the encoder is also recreated so the new worklet's
   * onmessage closure references a live encoder.
   *
   * Encoder timestamps reset to 0; that's fine because the decoder side
   * is stateless across worklet rebuilds and the bridge doesn't care
   * about uplink timestamp continuity.
   */
  async restartMic(opts: StartMicOptions = {}): Promise<void> {
    if (this.ctx === null) return
    if (this.micStream !== null) {
      for (const t of this.micStream.getTracks()) {
        try { t.stop() } catch { /* already stopped */ }
      }
      this.micStream = null
    }
    this.micWorklet?.disconnect()
    this.micWorklet = null
    if (this.encoder !== null) {
      try { this.encoder.close() } catch { /* already closed */ }
      this.encoder = null
    }
    this.encodeTsUs = 0
    await this.startMic(opts)
  }

  /** Self-diagnostics snapshot for the UI's status card. */
  getStats(): AudioStats {
    const t = this.kbpsTracker.totals()
    return {
      packetsSent: t.packets,
      bytesSent: t.bytes,
      kbpsRecent: this.kbpsTracker.kbps(),
      micPeak: this.lastPeak,
    }
  }

  /** Sets the display name shown on exported clip filenames. Called from the
   *  page when the snapshot roster updates. */
  setSpeakerLabel(agid: string, name: string): void {
    if (name.length === 0) this.speakerLabels.delete(agid)
    else this.speakerLabels.set(agid, name)
  }

  /**
   * Try to route playback to a specific output device (Chrome / Edge).
   * Safari does not implement AudioContext.setSinkId yet, so we silently
   * fall back to the system default. Returns true if applied.
   */
  async setOutputDeviceId(deviceId: string | null): Promise<boolean> {
    const ctx = this.ctx
    if (ctx === null) return false
    const anyCtx = ctx as AudioContext & { setSinkId?: (id: string | { type: 'none' }) => Promise<void> }
    if (typeof anyCtx.setSinkId !== 'function') return false
    try {
      await anyCtx.setSinkId(deviceId === null || deviceId.length === 0 ? '' : deviceId)
      return true
    } catch (err) {
      console.warn('setSinkId failed', err)
      return false
    }
  }

  /**
   * Capture the last CLIP_WINDOW_SECONDS of audio: per-speaker mono +
   * stereo mix. Returns linearised Float32Arrays (oldest sample first).
   * Used by the moderation clip-capture feature.
   */
  snapshotClipBuffer(): ClipBufferSnapshot {
    const perSpeaker = new Map<string, Float32Array>()
    for (const [agid, ring] of this.clipRings) {
      perSpeaker.set(agid, lineariseRing(ring.data, ring.writeIndex, ring.filled))
    }
    return {
      sampleRate: SAMPLE_RATE,
      capturedAt: new Date(),
      durationSeconds: CLIP_WINDOW_SECONDS,
      perSpeaker,
    }
  }

  /** Friendly label for a given AGID (display name if known, else AGID). */
  speakerLabel(agid: string): string {
    return this.speakerLabels.get(agid) ?? agid
  }

  /**
   * Push an inbound Opus packet for a specific sender. Caller supplies AGID
   * inline (the v1 binary AudioFwd format carries it) so we can lazy-register
   * the decoder + state without waiting for a voiceSession event.
   */
  ingestOpus(senderSessionId: number, agid: string, opus: Uint8Array): void {
    if (!this.sessionToAgid.has(senderSessionId)) {
      this.sessionToAgid.set(senderSessionId, agid)
    }
    const sender = this.senderFor(agid)
    sender.lastPacketMs = Date.now()
    try {
      sender.decoder.decode(new EncodedAudioChunk({
        type: 'key',
        timestamp: 0,
        data: opus,
      }))
    } catch (err) {
      console.warn('decode failed for', agid, err)
    }
  }

  /**
   * Inform the engine about a sessionId-to-AGID mapping from a voiceSession
   * event. Most of the time this arrives BEFORE the first AudioFwd frame
   * (snapshot WS is faster than UDP voice negotiation in practice on web).
   */
  onVoiceSession(sessionId: number, agid: string, active: boolean): void {
    if (active) {
      this.sessionToAgid.set(sessionId, agid)
    } else {
      this.sessionToAgid.delete(sessionId)
      this.forgetSender(agid)
    }
  }

  /**
   * Replace per-AGID spatial state from a snapshot frame.
   *
   * Each entry MAY carry listener-local position (localX/localY/localZ) in
   * cm. When present, those drive the PannerNode position so the browser's
   * HRTF puts the sender in the correct spot in 3D space. Distance gain is
   * derived from gainL/gainR (`hypot` preserves the bridge's distance falloff
   * because the constant-power pan keeps `gainL^2 + gainR^2 = distance^2`).
   *
   * Senders absent from `gains` keep their previous values until
   * {@link SPATIAL_GAIN_GRACE_MS} elapses since their last update, at which
   * point they're silenced. Without this grace period a single late snapshot
   * frame silenced everyone for one tick.
   */
  setSpatialGains(
    gains: ReadonlyMap<string, {
      gainL: number
      gainR: number
      localX?: number
      localY?: number
      localZ?: number
    }>,
  ): void {
    const now = Date.now()
    for (const [agid, g] of gains) {
      const sender = this.senderFor(agid)
      sender.gainL = g.gainL
      sender.gainR = g.gainR
      sender.distanceGain = Math.hypot(g.gainL, g.gainR)
      sender.lastGainUpdateMs = now
      if (g.localX !== undefined && g.localY !== undefined && g.localZ !== undefined) {
        this.applyPanPosition(sender, g.localX, g.localY, g.localZ)
      } else {
        // Fallback for old bridges: amplitude-pan via stereo gains by placing
        // the sender at unit distance along the bearing implied by the L/R
        // gain ratio. atan2(R-L, R+L) gives a rough azimuth substitute.
        const az = Math.atan2(g.gainR - g.gainL, Math.hypot(g.gainL, g.gainR))
        this.applyPanPosition(sender, Math.sin(az), Math.cos(az), 0)
      }
      this.refreshSenderAmplitude(sender)
    }
    // Senders absent from this snapshot's gains keep their last-known gain
    // (new senders default to unity). We deliberately DO NOT silence them: the
    // bridge proximity-gates what it forwards, so any audio still arriving is
    // meant to be heard. Re-gating here previously zeroed every sender whenever
    // the snapshot gains were empty or late (common on the web path), which made
    // the client hear no one despite frames arriving over the datachannel.
    for (const s of this.senders.values()) {
      if (gains.has(s.agid)) continue
      if (s.lastGainUpdateMs === 0) s.lastGainUpdateMs = now
    }
  }

  setSenderUserGain(agid: string, gain: number): void {
    const sender = this.senderFor(agid)
    sender.userGain = Math.max(0, Math.min(4, gain))
    this.refreshSenderAmplitude(sender)
  }

  setSenderMuted(agid: string, muted: boolean): void {
    const sender = this.senderFor(agid)
    sender.muted = muted
    this.refreshSenderAmplitude(sender)
  }

  setMasterGain(gain: number): void {
    this.masterGain = Math.max(0, Math.min(4, gain))
    for (const s of this.senders.values()) this.refreshSenderAmplitude(s)
  }

  /**
   * Push the latest distance / user / master / mute composite into the
   * sender's GainNode. Cheap (one AudioParam write); called from any code
   * path that changes a contributing input.
   */
  private refreshSenderAmplitude(sender: SenderState): void {
    const ctx = this.ctx
    if (ctx === null) return
    const linear = sender.muted ? 0 : sender.distanceGain * sender.userGain * this.masterGain
    // setTargetAtTime gives a small exponential ramp (10 ms) which prevents
    // an audible click when distance or user gain jumps between snapshot
    // ticks. With 50 packets/s underneath, the ramp doesn't smear voice.
    sender.gainNode.gain.setTargetAtTime(linear, ctx.currentTime, 0.01)
  }

  /**
   * Update the PannerNode position. Uses the modern positionX/positionY/
   * positionZ AudioParams where available and falls back to setPosition()
   * for older Safari builds.
   */
  private applyPanPosition(
    sender: SenderState,
    localX: number,
    localY: number,
    localZ: number,
  ): void {
    const ctx = this.ctx
    if (ctx === null) return
    const panner = sender.panner as PannerNode & {
      positionX?: AudioParam
      positionY?: AudioParam
      positionZ?: AudioParam
    }
    if (panner.positionX !== undefined) {
      const now = ctx.currentTime
      panner.positionX.setTargetAtTime(localX, now, 0.02)
      panner.positionY!.setTargetAtTime(localY, now, 0.02)
      panner.positionZ!.setTargetAtTime(localZ, now, 0.02)
    } else if (typeof sender.panner.setPosition === 'function') {
      sender.panner.setPosition(localX, localY, localZ)
    }
  }

  /** Snapshot of "is this AGID talking right now". Used by the UI to light
   *  up the talking dot. Cheap: O(1) lookup + 1 subtraction. */
  isTalking(agid: string): boolean {
    const sender = this.senders.get(agid)
    if (sender === undefined) return false
    return Date.now() - sender.lastPacketMs < TALKING_WINDOW_MS
  }

  /** All AGIDs we've ever heard from. UI uses this to know which roster
   *  rows should get talking-indicator polling. */
  knownSenderAgids(): ReadonlyArray<string> {
    return [...this.senders.keys()]
  }

  forgetSender(agid: string): void {
    const sender = this.senders.get(agid)
    if (sender === undefined) return
    try { sender.decoder.close() } catch { /* already closed */ }
    try { sender.source.disconnect() } catch { /* already disconnected */ }
    try { sender.gainNode.disconnect() } catch { /* already disconnected */ }
    try { sender.panner.disconnect() } catch { /* already disconnected */ }
    this.senders.delete(agid)
  }

  async dispose(): Promise<void> {
    for (const sender of this.senders.values()) {
      try { sender.decoder.close() } catch { /* ignore */ }
      try { sender.source.disconnect() } catch { /* ignore */ }
      try { sender.gainNode.disconnect() } catch { /* ignore */ }
      try { sender.panner.disconnect() } catch { /* ignore */ }
    }
    this.senders.clear()
    this.sessionToAgid.clear()
    try { this.encoder?.close() } catch { /* ignore */ }
    this.encoder = null
    if (this.micStream !== null) {
      for (const track of this.micStream.getTracks()) track.stop()
      this.micStream = null
    }
    this.micWorklet?.disconnect()
    if (this.masterLimiter !== null) {
      try { this.masterLimiter.disconnect() } catch { /* ignore */ }
      this.masterLimiter = null
    }
    if (this.ctx !== null) {
      try { await this.ctx.close() } catch { /* ignore */ }
      this.ctx = null
    }
  }

  private requireCtx(): AudioContext {
    const ctx = this.ctx
    if (ctx === null) throw new Error('AudioEngine.init() not called')
    return ctx
  }

  private senderFor(agid: string): SenderState {
    const existing = this.senders.get(agid)
    if (existing !== undefined) return existing
    const ctx = this.requireCtx()

    // mono-source: streams decoded Float32 frames into the graph.
    const source = new AudioWorkletNode(ctx, 'mono-source', {
      numberOfInputs: 0,
      numberOfOutputs: 1,
      outputChannelCount: [1],
    })

    // GainNode: amplitude (distance * user * master * !mute). Starts at 1
    // so the first packet before any snapshot frame plays at unity rather
    // than being silenced.
    const gainNode = ctx.createGain()
    gainNode.gain.value = 1

    // PannerNode: HRTF binaural. Distance attenuation is intentionally
    // disabled here (refDistance=very large + rolloffFactor=0) because the
    // bridge already computed and baked in distance falloff; we apply it
    // via the GainNode above. Letting PannerNode also attenuate would
    // double-dip and silence even close speakers.
    const panner = ctx.createPanner()
    panner.panningModel = 'HRTF'
    panner.distanceModel = 'linear'
    panner.refDistance = 1
    panner.maxDistance = 1_000_000
    panner.rolloffFactor = 0
    // Start at origin (= listener). First snapshot will reposition. Until
    // then the sender plays at the listener's head (i.e. centered + close)
    // which is the right behaviour for "I just heard them but don't know
    // where they are yet."
    if ((panner as { positionX?: AudioParam }).positionX !== undefined) {
      ;(panner as PannerNode & { positionX: AudioParam }).positionX.value = 0
      ;(panner as PannerNode & { positionY: AudioParam }).positionY.value = 0
      ;(panner as PannerNode & { positionZ: AudioParam }).positionZ.value = 0
    } else if (typeof panner.setPosition === 'function') {
      panner.setPosition(0, 0, 0)
    }

    source.connect(gainNode)
    gainNode.connect(panner)
    // Route through the master limiter (falls back to destination only if
    // init() somehow hasn't run, which it always has by the time a sender
    // exists). Prevents the summed, gain-boosted output from hard-clipping.
    panner.connect(this.masterLimiter ?? ctx.destination)

    const sender: SenderState = {
      agid,
      decoder: this.createDecoder(agid),
      source,
      gainNode,
      panner,
      distanceGain: 1,
      userGain: 1.0,
      muted: false,
      gainL: CENTER_GAIN,
      gainR: CENTER_GAIN,
      lastPacketMs: 0,
      lastGainUpdateMs: 0,
    }
    this.senders.set(agid, sender)
    return sender
  }

  private createDecoder(agid: string): AudioDecoder {
    const decoder = new AudioDecoder({
      output: (audioData) => this.onDecoded(agid, audioData),
      error: (e) => {
        console.warn('decode failed for', agid, e)
        this.opts.onCodecError?.('decoder', e)
        this.forgetSender(agid)
      },
    })
    decoder.configure(this.opts.opusConfig.decoder)
    return decoder
  }

  private onDecoded(agid: string, audioData: AudioData): void {
    const sender = this.senders.get(agid)
    if (sender === undefined) { audioData.close(); return }

    const sampleCount = audioData.numberOfFrames
    const mono = new Float32Array(sampleCount)
    audioData.copyTo(mono.buffer, { planeIndex: 0, format: 'f32-planar' })
    audioData.close()

    // Always record the raw mono into the per-speaker clip ring so the
    // moderation-capture window stays complete even for muted speakers
    // (the report should show what was said, not what the local listener
    // heard after their own attenuation).
    this.writeClipRing(agid, mono)

    // Hand the mono samples to this sender's source worklet. The graph
    // (GainNode -> PannerNode -> destination) handles amplitude + HRTF
    // placement; we don't pre-multiply here. Transfer the underlying
    // ArrayBuffer so we don't pay a copy per frame.
    sender.source.port.postMessage(mono.buffer, [mono.buffer])
  }

  private recordSentBytes(byteLength: number): void {
    this.kbpsTracker.record(byteLength)
  }

  private writeClipRing(agid: string, samples: Float32Array): void {
    let ring = this.clipRings.get(agid)
    if (ring === undefined) {
      if (this.clipRings.size >= CLIP_MAX_SPEAKERS) {
        // Evict the speaker we last heard from earliest, FIFO.
        let oldestAgid: string | null = null
        let oldestMs = Number.POSITIVE_INFINITY
        for (const [a, r] of this.clipRings) {
          if (r.lastWriteMs < oldestMs) { oldestMs = r.lastWriteMs; oldestAgid = a }
        }
        if (oldestAgid !== null) this.clipRings.delete(oldestAgid)
      }
      ring = { data: new Float32Array(CLIP_WINDOW_SAMPLES), writeIndex: 0, filled: false, lastWriteMs: 0 }
      this.clipRings.set(agid, ring)
    }
    writeRing(ring.data, ring, samples)
    ring.lastWriteMs = Date.now()
  }
}
